import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import mx from "@/locales/mx.json";
import en from "@/locales/en.json";

const DICTIONARIES = { mx, en };
export const SUPPORTED_LANGS = ["mx", "en"];
export const DEFAULT_LANG = "mx";

const I18nContext = createContext(null);

function resolveLangFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  if (SUPPORTED_LANGS.includes(seg)) return seg;
  return DEFAULT_LANG;
}

function getByPath(obj, keyPath) {
  return keyPath.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const lang = resolveLangFromPath(location.pathname);
  const dict = DICTIONARIES[lang] || DICTIONARIES[DEFAULT_LANG];

  const value = useMemo(() => {
    const t = (key, fallback = "") => {
      const val = getByPath(dict, key);
      return val === undefined ? (fallback || key) : val;
    };
    const switchLang = (nextLang) => {
      if (!SUPPORTED_LANGS.includes(nextLang) || nextLang === lang) return;
      const parts = location.pathname.split("/").filter(Boolean);
      if (SUPPORTED_LANGS.includes(parts[0])) {
        parts[0] = nextLang;
      } else {
        parts.unshift(nextLang);
      }
      const nextPath = "/" + parts.join("/");
      navigate(nextPath + location.search, { replace: false, state: { langSwitch: true } });
    };
    const localePath = (subpath = "") => {
      const clean = subpath.startsWith("/") ? subpath : `/${subpath}`;
      return `/${lang}${clean === "/" ? "" : clean}`;
    };
    return { lang, t, switchLang, localePath };
  }, [lang, dict, location.pathname, location.search, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
